import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/shared/ui/dialog';
import { Button } from '@/components/shared/ui/button';
import { Input } from '@/components/shared/ui/input';
import { Label } from '@/components/shared/ui/label';
import { useToast } from '@/components/shared/ui/use-toast';
import { Eye, EyeOff, ArrowLeft, CheckCircle2, X, Check } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { useUIStore } from '@/stores/useUIStore';
import { cn } from '@/lib/utils';

// Requisitos de contraseña segura
const PASSWORD_REQUIREMENTS = [
  { label: '8+ caracteres', regex: /.{8,}/ },
  { label: 'Una mayúscula', regex: /[A-Z]/ },
  { label: 'Un número', regex: /[0-9]/ },
  { label: 'Un carácter especial', regex: /[^A-Za-z0-9]/ },
];

interface AuthModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode?: 'login' | 'register' | 'forgot-password' | 'reset-password';
}

export const AuthModal = ({ isOpen, onOpenChange, defaultMode = 'login' }: AuthModalProps) => {
  const { recoveryToken } = useUIStore();
  const [mode, setMode] = useState<'login' | 'register' | 'forgot-password' | 'reset-password'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { login, register, resetPasswordForEmail, updatePassword, getToken, isAuthenticated } = useAuth();

  // Validación de requisitos en tiempo real
  const validations = PASSWORD_REQUIREMENTS.map(req => ({
    ...req,
    met: req.regex.test(password)
  }));

  const allMet = validations.every(v => v.met);
  const strength = validations.filter(v => v.met).length;

  const getStrengthColor = () => {
    if (strength <= 1) return 'bg-red-500';
    if (strength <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (password.length === 0) return '';
    if (strength <= 1) return 'Débil';
    if (strength <= 3) return 'Media';
    return 'Segura';
  };

  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const getTitle = () => {
    switch (mode) {
      case 'login': return '¡Bienvenido de nuevo!';
      case 'register': return 'Crea tu cuenta';
      case 'forgot-password': return 'Recuperar Contraseña';
      case 'reset-password': return 'Nueva Contraseña';
      default: return 'Autenticación';
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'login': return 'Ingresa tu email para acceder a tu cuenta';
      case 'register': return 'Completa los datos para crear tu cuenta';
      case 'forgot-password': return 'Te enviaremos un enlace para restablecer tu contraseña';
      case 'reset-password': return 'Ingresa tu nueva contraseña';
      default: return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        const response = await login({ email, password });

        if (response) {
          await queryClient.invalidateQueries({ queryKey: ['session'] });
        }

        toast({
          title: "¡Bienvenido!",
          description: "",
        });

        onOpenChange(false);
        resetForm();
      } else if (mode === 'register') {
        if (password !== confirmPassword) {
          throw new Error('Las contraseñas no coinciden');
        }

        await register({
          email,
          password,
          firstName,
          lastName,
        });

        toast({
          title: "¡Registro exitoso!",
          description: "Revisa tu email para confirmar tu cuenta.",
        });

        onOpenChange(false);
        resetForm();
      } else if (mode === 'forgot-password') {
        await resetPasswordForEmail(email);

        toast({
          title: "Email enviado",
          description: "Si el correo existe, recibirás instrucciones para restablecer tu contraseña.",
          action: <CheckCircle2 className="h-4 w-4 text-green-500" />
        });

      } else if (mode === 'reset-password') {
        if (password !== confirmPassword) {
          throw new Error('Las contraseñas no coinciden');
        }

        const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
        // Si hay un token explícito (recovery), lo usamos. Si no, updatePassword usará el de la cookie.
        const tokenStr = recoveryToken || hashParams.get('access_token') || getToken() || undefined;

        if (!tokenStr && !isAuthenticated) {
          throw new Error('No se pudo encontrar un token de sesión válido');
        }

        await updatePassword(password, tokenStr);

        toast({
          title: "Contraseña actualizada",
          description: "Tu contraseña ha sido cambiada exitosamente.",
        });

        // Si el usuario ya está autenticado, simplemente cerramos el modal
        if (isAuthenticated) {
          onOpenChange(false);
          resetForm();
        } else {
          // Si no está autenticado, lo llevamos a login
          setMode('login');
          setPassword('');
          setConfirmPassword('');
        }
        
        // Si estamos en la ruta de reset-password, limpiamos la URL
        if (window.location.pathname.includes('reset-password')) {
          window.history.replaceState({}, '', '/');
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);

      let errorMessage = error.message || "Ocurrió un error durante la autenticación.";
      const errorString = errorMessage.toLowerCase();

      if (errorString.includes("email not confirmed")) {
        errorMessage = "Tu correo no ha sido confirmado. Por favor revisa tu bandeja de entrada.";
      } else if (errorString.includes("invalid login credentials")) {
        errorMessage = "Credenciales inválidas o correo no confirmado. Verifica tu contraseña y revisa tu email.";
      } else if (errorMessage === 'Las contraseñas no coinciden') {
        errorMessage = "Las contraseñas no coinciden.";
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (newMode: 'login' | 'register' | 'forgot-password') => {
    setMode(newMode);
  };

  const handleGoogleAuth = () => {
    // Fix: usa VITE_API_URL si está configurado, o '/api' como ruta relativa
    // En producción con Vercel, '/api/*' se reescribe al backend via vercel.json
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    window.location.href = `${apiUrl}/auth/google`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[375px] md:max-w-[820px] p-0 gap-0 overflow-hidden border-none rounded-2xl bg-white shadow-2xl [&>button]:hidden">
        <DialogTitle className="sr-only">{getTitle()}</DialogTitle>
        <div className="grid grid-cols-1 md:grid-cols-2">

          {/* Left Side - Image */}
          <div className="hidden md:flex relative bg-black flex-col justify-end p-8 text-white min-h-[450px]">
            <div className="absolute inset-0">
              <img
                src="/images/MODAO.webp"
                alt="Fashion"
                className="w-full h-full object-cover opacity-75"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            </div>

            <div className="relative z-10 space-y-3 mb-2">
              <img
                src="/images/LOGOMATCH_opt.webp"
                alt="MatchActive"
                className="h-5 w-auto object-contain mb-4 brightness-[10] opacity-90"
              />
              <h3 className="text-lg font-medium leading-relaxed font-montserrat">
                Match Active es una tienda de moda online que ofrece ropa, zapatos, bolsos y accesorios para hombres y mujeres.
              </h3>
              <div className="flex gap-2 pt-1">
                <div className="h-[3px] w-6 bg-white rounded-full" />
                <div className="h-[3px] w-6 bg-white/35 rounded-full" />
                <div className="h-[3px] w-6 bg-white/35 rounded-full" />
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-6 md:p-8 flex flex-col justify-center relative bg-white">
            {/* Close Button */}
            <button
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-50 text-gray-400 hover:text-gray-700 outline-none focus:outline-none ring-0 focus:ring-0 border-none focus-visible:ring-0 focus-visible:outline-none"
              type="button"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-full max-w-sm mx-auto space-y-4">

              {/* Logo (mobile only) */}
              <div className="flex justify-center md:hidden">
                <img
                  src="/images/LOGOMATCH_opt.webp"
                  alt="MatchActive"
                  className="h-6 w-auto object-contain"
                />
              </div>

              {/* Header */}
              <div className="space-y-1">
                {mode === 'forgot-password' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-0 h-auto text-gray-400 hover:text-black -ml-1 mb-1"
                    onClick={() => setMode('login')}
                  >
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Volver
                  </Button>
                )}
                <h2 className="text-2xl font-semibold tracking-tight text-gray-900 font-jost">
                  {getTitle()}
                </h2>
                <p className="text-sm text-gray-500">
                  {getSubtitle()}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">

                {/* Name fields (Register only) */}
                {mode === 'register' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">Nombre</Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="Tu nombre"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="h-10 rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 transition-all text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">Apellido</Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Tu apellido"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="h-10 rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 transition-all text-sm"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Email */}
                {mode !== 'reset-password' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="nombre@ejemplo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-10 rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 transition-all text-sm"
                      required
                      autoComplete="email"
                    />
                  </div>
                )}

                {/* Password */}
                {(mode === 'login' || mode === 'register' || mode === 'reset-password') && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                        {mode === 'reset-password' ? 'Nueva Contraseña' : 'Contraseña'}
                      </Label>
                      {mode === 'login' && (
                        <button
                          type="button"
                          onClick={() => setMode('forgot-password')}
                          className="text-xs text-gray-500 hover:text-black transition-colors underline-offset-4 hover:underline"
                        >
                          ¿Olvidaste tu contraseña?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-10 rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 transition-all pr-10 text-sm"
                        required
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      />
                      <button
                        type="button"
                        className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>

                    {/* Fortaleza de contraseña y requisitos */}
                    {(mode === 'register' || mode === 'reset-password') && password.length > 0 && (
                      <div className="space-y-3 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
                        {/* Barra de progreso */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center text-[10px] uppercase tracking-wider font-bold">
                            <span className="text-gray-400">Fortaleza:</span>
                            <span className={cn(
                              getStrengthText() === 'Débil' ? 'text-red-500' : 
                              getStrengthText() === 'Media' ? 'text-yellow-600' : 'text-green-600'
                            )}>
                              {getStrengthText()}
                            </span>
                          </div>
                          <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden flex gap-0.5">
                            {[1, 2, 3, 4].map((step) => (
                              <div 
                                key={step}
                                className={cn(
                                  "h-full flex-1 transition-all duration-500",
                                  step <= strength ? getStrengthColor() : "bg-gray-200"
                                )}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Lista de requisitos */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-1">
                          {validations.map((val, i) => (
                            <div key={i} className="flex items-center gap-2">
                              {val.met ? (
                                <div className="bg-green-100 p-0.5 rounded-full">
                                  <Check className="h-2.5 w-2.5 text-green-600 stroke-[3]" />
                                </div>
                              ) : (
                                <div className="bg-gray-100 p-0.5 rounded-full">
                                  <X className="h-2.5 w-2.5 text-gray-400 stroke-[3]" />
                                </div>
                              )}
                              <span className={cn(
                                "text-[11px] transition-colors font-medium",
                                val.met ? "text-green-600" : "text-gray-400"
                              )}>
                                {val.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Confirm Password (Register / Reset) */}
                {(mode === 'register' || mode === 'reset-password') && (
                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">Confirmar Contraseña</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="h-10 rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 transition-all pr-10 text-sm"
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  className={cn(
                    "w-full h-10 rounded-lg text-sm font-medium transition-all mt-1",
                    "bg-black text-white hover:bg-gray-800 active:scale-[0.99]",
                    (mode === 'register' || mode === 'reset-password') && !allMet && "opacity-50 cursor-not-allowed grayscale-[0.5]"
                  )}
                  disabled={loading || ((mode === 'register' || mode === 'reset-password') && !allMet)}
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white mr-2" />
                  )}
                  {mode === 'login' && 'Iniciar sesión'}
                  {mode === 'register' && (allMet ? 'Crear cuenta' : 'Contraseña demasiado débil')}
                  {mode === 'forgot-password' && 'Enviar enlace'}
                  {mode === 'reset-password' && (allMet ? 'Actualizar contraseña' : 'Contraseña demasiado débil')}
                </Button>
              </form>

              {/* Social Auth (Login / Register only) */}
              {(mode === 'login' || mode === 'register') && (
                <div className="space-y-3">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-100" />
                    </div>
                    <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                      <span className="bg-white px-3 text-gray-600 font-semibold">
                        o continúa con
                      </span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full h-10 rounded-lg border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all text-sm font-medium text-gray-700"
                    onClick={handleGoogleAuth}
                    type="button"
                  >
                    <img
                      src="https://www.svgrepo.com/show/475656/google-color.svg"
                      alt="Google"
                      className="w-4 h-4 mr-2 flex-shrink-0"
                    />
                    Google
                  </Button>
                </div>
              )}

              {/* Footer toggle */}
              <div className="text-center">
                {mode === 'login' ? (
                  <p className="text-sm text-gray-500">
                    ¿No tienes cuenta?{' '}
                    <button
                      onClick={() => handleModeChange('register')}
                      className="font-semibold text-black hover:underline underline-offset-4"
                    >
                      Regístrate
                    </button>
                  </p>
                ) : mode === 'register' ? (
                  <p className="text-sm text-gray-500">
                    ¿Ya tienes cuenta?{' '}
                    <button
                      onClick={() => handleModeChange('login')}
                      className="font-semibold text-black hover:underline underline-offset-4"
                    >
                      Iniciar sesión
                    </button>
                  </p>
                ) : null}
              </div>

            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
